import { UserManager } from 'oidc-client-ts';
import { AuthProvider } from 'ra-core';
import axios from '@/lib/axios'
import { stringify } from '@/utils/queryStringWrapper'
import { AccessTokenClaims, CastedUserManager, userManager } from './odUserManager'
import { parseAccessToken } from './jwtDecode'
import { storeTokens } from './storeTokens'
import { userApiClient } from '@/lib/client'
import retrieveAxiosErr from '@/utils/retrieveAxiosErr';

/**
 * Loose shape returned by the OIDC token endpoint. Replaces v1's
 * `UserSettings` type (which was a private `oidc-client` export and is
 * removed in `oidc-client-ts`).
 */
interface TokenEndpointResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type?: string;
  scope?: string;
  expires_in?: number;
  expires_at?: number;
  profile?: AccessTokenClaims;
  [k: string]: unknown;
}

export interface ActivateAutoLoginInput {
  type: 'ActivateAutoLogin';
  method: 'RegisterActivate' | 'InvitationActivate';
  username: string;
  password: string;
  vCode: string;
}

interface DeviceFlowInfo {
  usercode: string
}
export interface MfaLoginInput {
  type: 'MfaLogin';
  username: string;
  password: string;
  loginId: number;
  vCode: string;
  vCodeId: number;
  mfaWay: number;
  deviceFlow?: DeviceFlowInfo;
}

export interface CustomerIdPRedirectLoginInput {
  type: 'CustomerIdPRedirectLogin';
  username: string;
  code: string;
  ssoProtocol: 'oidc' | 'saml';
  deviceFlow?: DeviceFlowInfo;
}

export type LoginInput =
  | ActivateAutoLoginInput
  | MfaLoginInput
  | CustomerIdPRedirectLoginInput

export type OdAuthProvider = {
  [P in keyof AuthProvider as string extends P ? never : number extends P ? never : P]-?: AuthProvider[P]
}
& { userManager: UserManager }


// usually, authProvider should not be directly used in your component; you should use the hook methods that wraps this utility.
const authProvider: OdAuthProvider = {
    login: (input: LoginInput) => {
      switch (input.type) {
        case 'ActivateAutoLogin':
          return afterActivateAutoLogin(input);
        // case 'OnDataLocalAccountLogin':
          // login phase 1, we challenge user with password, then give him a verification code
          // we are not actually going to initial phase 1 here, but document it here instead
        case 'MfaLogin':
          // login phase 2, we challenge user with verifaction code distributed via sms/email, 
          // then exchange id_token/access_token with auth_code
          return mfaLogin(input);
        case 'CustomerIdPRedirectLogin':
          return ssoLogin(input);
        default:
          return Promise.resolve();
      }

    },
    logout: async (x: {stayAfterLogout?:boolean}) => {
      await userManager.removeUser()

      // Todo: when user login with his organization's SSO IdP, we should invoke SSO sign out endpoint
      // Todo: when user login with OnData local IdP, we should invoke OnData's sign out endpoint
      // eventually, after logout, user will be redirected to login page
      // if (!window.location.pathname.startsWith('/login')) {
      //   window.location.replace('/login')
      // }
      if (x?.stayAfterLogout) { return false }
      return '/login'
    },
    checkError: (error) => {
      const status = error.status || error.body?.status;
      if (status === 401) {
        return Promise.reject();
      }
      if (status === 403) {
        // return Promise.reject({ logoutUser: false, redirectTo: '', });
        return Promise.resolve();
      }
      // other error code (404, 500, etc): no need to log out
      return Promise.resolve();
    },
    checkAuth: async () => {
      const user = await userManager.getUser()
      const [success, failAndLogin] = [() => Promise.resolve(), () => Promise.reject({ redirectTo: '/login' })]
      
      if (!user) { return failAndLogin() } 
      else if(user.expired) { 
        return (userManager as unknown as CastedUserManager)._inspectRefreshedStatus().then(success, failAndLogin) 
      } 
      return success()
    },
    getPermissions: async (params: any): Promise<ReadonlyArray<string>> => {
      const casted = userManager as unknown as CastedUserManager
      const user = await userManager.getUser()
      if (!user || user.expired) { 
        return [] 
      }

      try
      {
        const jwtAt = parseAccessToken<AccessTokenClaims>(user.access_token).payload

        return (!jwtAt || !jwtAt.permissions) ? []
        : jwtAt.permissions instanceof Array  ? jwtAt.permissions
        : [jwtAt.permissions]

      } catch (err) {
        return []
      }
    },
    getIdentity: () => userApiClient.apiUsersProfileGet().then(resp => ({
      ...resp.data,
      id: resp.data.userId || -1,
    })),
    userManager: userManager,
};

export default authProvider;


const afterActivateAutoLogin = async (input: ActivateAutoLoginInput) => {
  try
  {
    const url = await userManager.metadataService.getTokenEndpoint()
    
    const resp = await axios.post<TokenEndpointResponse>(url || '', stringify({
      grant_type: 'password',
      username: input.username,
      password: input.password,
      vCode: input.vCode,
      loginType: input.method === 'RegisterActivate' ? 1 : 3 /* InvitationActivate */,
      client_id: 'ondata-portal',
    }), { headers: { Accept: 'application/json' } });

    await storeTokens(resp.data)
  }
  catch (err) {
    const errMsg = retrieveAxiosErr(err)
    return Promise.reject(errMsg)
  }
}

const mfaLogin = async (input: MfaLoginInput) => {
  try
  {
    const url  = await userManager.metadataService.getTokenEndpoint()
    
    const resp = await axios.post<TokenEndpointResponse>(url || '', stringify({
      grant_type: 'password',
      username: input.username,
      password: input.password,
      loginId: input.loginId,
      vCode: input.vCode,
      vCodeId: input.vCodeId,
      mfaWay: input.mfaWay,
      loginType: 0,
      client_id: 'ondata-portal',
    }), { headers: { Accept: 'application/json' } })

    await storeTokens(resp.data)
  }
  catch (err) {
    const errMsg = retrieveAxiosErr(err)
    return Promise.reject(errMsg)
  }
}

const ssoLogin = async (input: CustomerIdPRedirectLoginInput) => {
  try
  {
    const url = await userManager.metadataService.getTokenEndpoint()
    
    const resp = await axios.post<TokenEndpointResponse>(url || '', stringify({
      grant_type: 'password',
      username: input.username,
      password: input.username,
      vCode: input.code,
      loginType: input.ssoProtocol === 'saml' ? 4 : 5,
      client_id: 'ondata-portal',
    }), { headers: { Accept: 'application/json' } })

    await storeTokens(resp.data)
  }
  catch (err) {
    const errMsg = retrieveAxiosErr(err)
    return Promise.reject(errMsg)
  }
}


